import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Chip, Modal, Skeleton, StateBlock, useToast } from '@w7/ui'
import { useState } from 'react'
import {
  cmApi,
  type PlayerConsoleMfaMethod,
  type PlayerConsoleMfaRow,
} from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { isStaffCaller } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import {
  methodLabel,
  methodTone,
  playerConfirmCopy,
  type PlayerConsoleMfaConfirmIntent,
} from './consoleMfaCopy'
import './console-mfa.css'

async function loadPlayerRow(
  playerId: string,
  username?: string,
): Promise<PlayerConsoleMfaRow | null> {
  const q = username?.trim() || undefined
  const uname = q?.toLowerCase()
  const first = await cmApi.playerConsoleMfaList(authHeaders(), {
    q,
    page: 1,
    pageSize: 50,
  })
  // Hierarchy list passes agent_nodes.id — portal MFA uses player.users.id.
  // Prefer id match; fall back to username (Lab/Strix link).
  const hit =
    first.rows.find((r) => r.playerId.toLowerCase() === playerId.toLowerCase()) ??
    (uname
      ? first.rows.find((r) => r.username.trim().toLowerCase() === uname)
      : undefined)
  if (hit) return hit
  if (!q) return null
  const retry = await cmApi.playerConsoleMfaList(authHeaders(), {
    page: 1,
    pageSize: 50,
  })
  return (
    retry.rows.find((r) => r.playerId.toLowerCase() === playerId.toLowerCase()) ??
    (uname
      ? retry.rows.find((r) => r.username.trim().toLowerCase() === uname) ?? null
      : null)
  )
}

/**
 * Owner-CM portal player 2FA — status + turn-off (players enroll themselves).
 */
export function PlayerConsoleMfaControl({
  playerId,
  username,
}: {
  playerId: string
  username?: string
}) {
  const me = useCmMe()
  const toast = useToast()
  const qc = useQueryClient()
  const [intent, setIntent] = useState<PlayerConsoleMfaConfirmIntent | null>(null)

  const ownerOnly = Boolean(me.data) && !isStaffCaller(me.data?.caller)

  const rowQ = useQuery({
    queryKey: ['cm', 'console-mfa', 'player-row', playerId],
    enabled: ownerOnly && Boolean(playerId),
    queryFn: () => loadPlayerRow(playerId, username),
  })

  const setM = useMutation({
    mutationFn: (body: {
      action: PlayerConsoleMfaConfirmIntent['kind']
      method?: PlayerConsoleMfaMethod
    }) => {
      // Prefer portal user id from loaded row (hierarchy id ≠ player.users id).
      const portalId = rowQ.data?.playerId || playerId
      return cmApi.playerConsoleMfaSet(authHeaders(), {
        playerId: portalId,
        action: body.action,
        method: body.method,
        confirm: true,
      })
    },
    onSuccess: async (res) => {
      toast.success(res.message)
      setIntent(null)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'player-row', playerId] }),
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'players'] }),
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'downline-status'] }),
      ])
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  if (!ownerOnly) return null

  const row = rowQ.data ?? null
  const copy = intent ? playerConfirmCopy(intent) : null
  const busy = setM.isPending

  return (
    <div className="cm-mfa-control" data-testid="player-console-mfa-control">
      <div className="cm-mfa-control-head">
        <strong>Player portal 2FA</strong>
        <span className="cm-hint">App / SMS / recovery · owner CM only</span>
      </div>

      {rowQ.isLoading ? (
        <Skeleton height={72} width="100%" radius={6} />
      ) : rowQ.isError ? (
        <StateBlock icon="error" title="Could not load 2FA status" tone="error">
          {(rowQ.error as Error)?.message || 'Request failed.'}
          <div style={{ marginTop: 8 }}>
            <Button type="button" variant="secondary" size="sm" onClick={() => void rowQ.refetch()}>
              Retry
            </Button>
          </div>
        </StateBlock>
      ) : !row ? (
        <p className="cm-hint" role="status">
          No portal login found for this member yet.
        </p>
      ) : (
        <>
          <div className="cm-mfa-control-status">
            <Chip size="sm" tone={row.mfaEnabled ? 'profit' : 'muted'}>
              {row.mfaEnabled ? 'ON' : 'OFF'}
            </Chip>
            {row.setupRequired ? (
              <Chip size="sm" tone="warn">
                Setup next login
              </Chip>
            ) : null}
            {row.methods.map((m) => (
              <button
                key={m}
                type="button"
                className="cm-mfa-method-chip"
                title={`Remove ${methodLabel(m)}`}
                disabled={!row.found || busy}
                onClick={() => setIntent({ kind: 'disable-method', row, method: m })}
              >
                <Chip size="sm" tone={methodTone(m)}>
                  {methodLabel(m)} ✕
                </Chip>
              </button>
            ))}
          </div>

          <p className="cm-hint" role="status">
            {row.mfaEnabled
              ? 'On — portal App / SMS / recovery are active. Turn off to clear factors (sessions revoked).'
              : row.setupRequired
                ? 'Off — setup required: player must enroll App or SMS on the player site (CM cannot scan the QR for them).'
                : 'Off — use Turn ON (require setup) so they must enroll on the player site. CM cannot enroll authenticator for them.'}
          </p>

          <div className="cm-mfa-actions">
            {row.mfaEnabled ? (
              <Button
                type="button"
                variant="danger"
                size="sm"
                disabled={!row.found || busy}
                onClick={() => setIntent({ kind: 'disable-all', row })}
              >
                Turn OFF
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!row.found || busy}
                onClick={() => setIntent({ kind: 'require-setup', row })}
              >
                Turn ON (require setup)
              </Button>
            )}
            {row.setupRequired ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setIntent({ kind: 'clear-setup', row })}
              >
                Clear force
              </Button>
            ) : null}
          </div>
        </>
      )}

      {intent && copy ? (
        <Modal
          open
          onClose={() => {
            if (!busy) setIntent(null)
          }}
          title={copy.title}
          subtitle="Company Master security control — portal player factors"
          closeDisabled={busy}
          footer={
            <div className="cm-mfa-modal-footer">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setIntent(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant={copy.danger ? 'danger' : 'primary'}
                busy={busy}
                onClick={() =>
                  setM.mutate({
                    action: intent.kind as
                      | 'disable-all'
                      | 'disable-method'
                      | 'require-setup'
                      | 'clear-setup',
                    method: intent.kind === 'disable-method' ? intent.method : undefined,
                  })
                }
              >
                {copy.confirmLabel}
              </Button>
            </div>
          }
        >
          <p className="cm-mfa-modal-body">{copy.body}</p>
          <div className="cm-mfa-modal-meta">
            <div>
              <span className="cm-hint">Player</span>
              <strong>{intent.row.username || intent.row.displayName}</strong>
            </div>
            <div>
              <span className="cm-hint">Factors now</span>
              <strong>
                {intent.row.methods.length
                  ? intent.row.methods.map(methodLabel).join(', ')
                  : 'None'}
              </strong>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
